import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getAvatarVariantUrl } from '../../utils/avatarVariants';
import './ActivitySocialBar.css';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * ActivitySocialBar - Kudos button + comments section for completed activities.
 * Works in both personal feed (Activities.js) and social feed (SocialActivityFeed.js).
 *
 * Props:
 *   activityId   - The activity's _id
 *   social       - { kudos_count, comment_count, has_kudos } from activity.social
 *   backendService
 *   compact                - If true, show only counts inline (for collapsed rows)
 *   commentsAlwaysExpanded - If true, load comments immediately and never collapse them
 */
const ActivitySocialBar = ({ activityId, social, backendService, compact = false, isOwner = false, activityStatus = 'complete', commentsAlwaysExpanded = false }) => {
  const [kudosCount, setKudosCount] = useState(social?.kudos_count || 0);
  const [hasKudos, setHasKudos] = useState(social?.has_kudos || false);
  const [commentCount, setCommentCount] = useState(social?.comment_count || 0);
  const [showComments, setShowComments] = useState(commentsAlwaysExpanded);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [kudosAnimating, setKudosAnimating] = useState(false);
  const [showKudosList, setShowKudosList] = useState(false);
  const [kudosList, setKudosList] = useState([]);
  const [loadingKudos, setLoadingKudos] = useState(false);
  const commentInputRef = useRef(null);
  const loadingCommentsRef = useRef(false);
  const commentsId = `activity-comments-${activityId}`;

  const currentUserId = useMemo(() => {
    const user = backendService.getUser ? backendService.getUser() : null;
    if (!user) return null;
    return user._id || user.user_id || user.id || null;
  }, [backendService]);

  // Sync from props when they change
  useEffect(() => {
    setKudosCount(social?.kudos_count || 0);
    setHasKudos(social?.has_kudos || false);
    setCommentCount(social?.comment_count || 0);
  }, [social]);

  const handleToggleKudos = useCallback(async (e) => {
    e.stopPropagation();
    try {
      setKudosAnimating(true);
      const result = await backendService.toggleKudos(activityId);
      setHasKudos(typeof result.kudos === 'boolean' ? result.kudos : result.action === 'added');
      setKudosCount(result.kudos_count);
      setTimeout(() => setKudosAnimating(false), 400);
    } catch (err) {
      console.error('Error toggling kudos:', err);
      setKudosAnimating(false);
    }
  }, [activityId, backendService]);

  const fetchComments = useCallback(async () => {
    if (loadingCommentsRef.current) return;
    loadingCommentsRef.current = true;
    setLoadingComments(true);
    try {
      const data = await backendService.getActivityComments(activityId);
      const list = Array.isArray(data) ? data : (data.comments || []);
      setComments(list);
      setCommentCount(list.length);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      loadingCommentsRef.current = false;
      setLoadingComments(false);
    }
  }, [activityId, backendService]);

  useEffect(() => {
    if (commentsAlwaysExpanded) {
      setShowComments(true);
      fetchComments();
    }
  }, [commentsAlwaysExpanded, fetchComments]);

  const handleShowComments = useCallback((e) => {
    e.stopPropagation();
    if (commentsAlwaysExpanded) {
      commentInputRef.current?.focus();
      return;
    }
    if (!showComments) {
      setShowComments(true);
      fetchComments();
    } else {
      setShowComments(false);
    }
  }, [commentsAlwaysExpanded, showComments, fetchComments]);

  const handleSubmitComment = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = commentText.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const result = await backendService.addActivityComment(activityId, text);
      const newComment = result.comment || result;
      if (newComment && newComment._id) {
        setComments(prev => [...prev, newComment]);
        setCommentCount(prev => prev + 1);
      }
      setCommentText('');
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSubmitting(false);
    }
  }, [activityId, backendService, commentText, submitting]);

  const handleDeleteComment = useCallback(async (commentId, e) => {
    e.stopPropagation();
    try {
      await backendService.deleteActivityComment(commentId);
      setComments(prev => prev.filter(c => c._id !== commentId));
      setCommentCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  }, [backendService]);

  const handleShowKudosList = useCallback(async (e) => {
    e.stopPropagation();
    if (kudosCount === 0) return;
    if (showKudosList) {
      setShowKudosList(false);
      return;
    }
    setShowKudosList(true);
    setLoadingKudos(true);
    try {
      const data = await backendService.getActivityKudos(activityId);
      setKudosList(Array.isArray(data) ? data : data.kudos || []);
    } catch (err) {
      console.error('Error loading kudos list:', err);
    } finally {
      setLoadingKudos(false);
    }
  }, [activityId, backendService, kudosCount, showKudosList]);

  // Focus only after an explicit user expansion. Always-open social chat must
  // not steal focus or summon a mobile keyboard when the detail modal opens.
  useEffect(() => {
    if (!showComments || commentsAlwaysExpanded || !commentInputRef.current) return undefined;
    const focusTimer = setTimeout(() => commentInputRef.current?.focus(), 100);
    return () => clearTimeout(focusTimer);
  }, [commentsAlwaysExpanded, showComments]);

  // Compact mode: just show inline counts (for collapsed activity rows)
  if (compact) {
    const total = kudosCount + commentCount;
    if (total === 0) return null;
    return (
      <span className="social-bar-compact" onClick={e => e.stopPropagation()}>
        {kudosCount > 0 && (
          <span
            className={`social-compact-kudos ${hasKudos ? 'active' : ''} ${(isOwner || activityStatus !== 'complete') ? 'read-only' : ''}`}
            onClick={(isOwner || activityStatus !== 'complete') ? undefined : handleToggleKudos}
          >
            👊 {kudosCount}
          </span>
        )}
        {commentCount > 0 && (
          <span className="social-compact-comments">
            💬 {commentCount}
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="social-bar" onClick={e => e.stopPropagation()}>
      {/* Action buttons row */}
      <div className="social-bar-actions">
        {!isOwner && activityStatus === 'complete' && (
          <button
            className={`social-bar-btn kudos-btn ${hasKudos ? 'active' : ''} ${kudosAnimating ? 'animating' : ''}`}
            onClick={handleToggleKudos}
            title={hasKudos ? 'Remove kudos' : 'Give kudos'}
          >
            <span className="kudos-icon">👊</span>
            <span className="social-bar-btn-label">
              {hasKudos ? 'Kudos given' : 'Kudos'}
            </span>
          </button>
        )}
        {kudosCount > 0 && (
          <span className="social-bar-kudos-count" onClick={handleShowKudosList} title="See who gave kudos">
            👊 {kudosCount}
            {showKudosList && (
              <div className="kudos-list-dropdown">
                {loadingKudos ? (
                  <div className="kudos-list-loading">Loading...</div>
                ) : (
                  kudosList.map(k => (
                    <div key={k._id} className="kudos-list-item">
                      {k.user?.avatar_url ? (
                        <img src={getAvatarVariantUrl(k.user, 'xs')} alt="" className="kudos-avatar" />
                      ) : (
                        <div className="kudos-avatar-placeholder">
                          {(k.user?.display_name || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <span className="kudos-name">{k.user?.display_name || 'Someone'}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </span>
        )}

        {commentsAlwaysExpanded ? (
          <span
            className="social-bar-btn comment-btn active static"
            aria-label={`${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}`}
          >
            <span className="comment-icon">💬</span>
            <span className="social-bar-btn-label">
              Comments {commentCount}
            </span>
          </span>
        ) : (
          <button
            className={`social-bar-btn comment-btn ${showComments ? 'active' : ''}`}
            onClick={handleShowComments}
            title="Comments"
            aria-expanded={showComments}
            aria-controls={commentsId}
          >
            <span className="comment-icon">💬</span>
            <span className="social-bar-btn-label">
              Comments {commentCount}
            </span>
          </button>
        )}
      </div>

      {/* Comments section */}
      {showComments && (
        <div id={commentsId} className="social-bar-comments">
          {loadingComments ? (
            <div className="comments-loading">Loading comments...</div>
          ) : (
            <>
              {comments.length === 0 && (
                <div className="comments-empty">No comments yet. Be the first!</div>
              )}
              {comments.map(comment => (
                <div key={comment._id} className="comment-item">
                  <div className="comment-header">
                    {comment.user?.avatar_url ? (
                      <img src={getAvatarVariantUrl(comment.user, 'xs')} alt="" className="comment-avatar" />
                    ) : (
                      <div className="comment-avatar-placeholder">
                        {(comment.user?.display_name || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <span className="comment-author">{comment.user?.display_name || 'Someone'}</span>
                    <span className="comment-time">{formatRelativeTime(comment.created_at)}</span>
                    {currentUserId && String(comment.user_id || comment.user?._id) === String(currentUserId) && (
                      <button
                        className="comment-delete"
                        onClick={(e) => handleDeleteComment(comment._id, e)}
                        title="Delete comment"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="comment-text">{comment.text}</div>
                </div>
              ))}
            </>
          )}

          {/* Comment input */}
          <form className="comment-input-form" onSubmit={handleSubmitComment}>
            <input
              ref={commentInputRef}
              type="text"
              className="comment-input"
              placeholder="Write a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              maxLength={500}
              disabled={submitting}
              onClick={e => e.stopPropagation()}
            />
            <button
              type="submit"
              className="comment-submit"
              disabled={!commentText.trim() || submitting}
            >
              {submitting ? '...' : '→'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ActivitySocialBar;
